import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getSession, signIn, signOut, SessionInfo, ADMIN_GROUP } from '../auth';

export interface AuthUser {
  username: string;
  name: string;
  email: string;
  groups: string[];
}

interface AuthContextValue {
  loading: boolean;
  user: AuthUser | null;
  isAdmin: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  loading: true,
  user: null,
  isAdmin: false,
  login: async () => {},
  logout: async () => {},
});

export const useAuth = (): AuthContextValue => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const loadingRef = useRef(false);

  const loadSession = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const session: SessionInfo | null = await getSession();
      if (session) {
        setUser({
          username: session.username,
          name: session.name,
          email: session.email,
          groups: session.groups,
        });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadSession();

    // Listen for Amplify auth events (OAuth callback code exchange)
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          loadSession();
          break;
        case 'signInWithRedirect_failure':
          console.error('OAuth sign-in failed:', payload.data);
          setLoading(false);
          break;
        case 'tokenRefresh':
          loadSession();
          break;
      }
    });

    return unsubscribe;
  }, [loadSession]);

  const login = useCallback(async () => {
    await signIn();
  }, []);

  const logout = useCallback(async () => {
    await signOut();
    setUser(null);
  }, []);

  const isAdmin = user?.groups.includes(ADMIN_GROUP) ?? false;

  return (
    <AuthContext.Provider value={{ loading, user, isAdmin, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
